/**
 * @module tier-gate
 * @description Declares the shared pricing-tier gating rules used by the
 * adapters package. The module maps features and packages to project tiers for
 * setup-time and runtime availability checks.
 */

/**
 * Supported project pricing tiers in ascending order of capability.
 * Determines which features and packages are available to a project.
 */
export type ProjectTier = "pronto" | "lancio" | "studio";

/**
 * Configuration object defining feature and package access per tier.
 * Features and packages are cumulative - higher tiers inherit from lower tiers.
 * This means:
 * - lancio includes all pronto features + lancio-specific features
 * - studio includes all pronto + lancio features + studio-specific features
 */
export interface TierGateConfig {
  /** Set of features available for each tier (key examples: 'auth', 'payments', 'ai-chat') */
  features: Record<ProjectTier, readonly string[]>;
  /** NPM packages available for each tier (key examples: '@geenius/admin', '@geenius/payments') */
  packages: Record<ProjectTier, readonly string[]>;
}

/** Readonly tier gating configuration used for frozen default tier records. */
export type ReadonlyTierGateConfig = Readonly<{
  features: Readonly<Record<ProjectTier, readonly string[]>>;
  packages: Readonly<Record<ProjectTier, readonly string[]>>;
}>;

/**
 * Result returned by requireTier() for feature availability checks.
 * The shape is intentionally explicit so UI code can render upgrade prompts
 * without throwing for normal "not available on this tier" outcomes.
 */
export interface TierGateResult {
  /** True when the requested feature is available at the current tier. */
  ok: boolean;
  /** Current project tier that was checked. */
  tier: ProjectTier;
  /** Feature key that was requested. */
  feature: string;
  /** The first tier where the feature becomes available, when known. */
  requiredTier?: ProjectTier;
  /** Human-readable explanation for failed checks. */
  reason?: string;
}

const TIER_ORDER: readonly ProjectTier[] = ["pronto", "lancio", "studio"];
const TIER_LABELS: Record<ProjectTier, string> = {
  pronto: "Pronto",
  lancio: "Lancio",
  studio: "Studio",
};

function freezeTierItems(items: readonly string[]): readonly string[] {
  return Object.freeze([...items]);
}

function freezeTierRecord(
  record: Record<ProjectTier, readonly string[]>,
): Readonly<Record<ProjectTier, readonly string[]>> {
  return Object.freeze({
    pronto: freezeTierItems(record.pronto),
    lancio: freezeTierItems(record.lancio),
    studio: freezeTierItems(record.studio),
  });
}

function freezeTierGateConfig(config: TierGateConfig): ReadonlyTierGateConfig {
  return Object.freeze({
    features: freezeTierRecord(config.features),
    packages: freezeTierRecord(config.packages),
  });
}

/**
 * Default tier gating configuration for geenius applications.
 * Defines all available features and packages organized by tier.
 *
 * Tier progression:
 * - pronto: Core features for rapid prototyping
 * - lancio: Production-ready with payments, admin, AI
 * - studio: Full-featured with advanced AI, analytics, teams, etc.
 */
export const DEFAULT_TIER_GATE: ReadonlyTierGateConfig = freezeTierGateConfig({
  // Available features (UI-level components and functionality)
  features: {
    pronto: [
      // Core auth and i18n
      "auth",
      "i18n",
      "ai-chat",
    ],
    lancio: [
      // Inherit pronto features
      "auth",
      "i18n",
      "ai-chat",
      // Add production features
      "admin",
      "payments",
      "blog",
      "file-storage",
      "ai-workflow",
      "ai-embeddings",
    ],
    studio: [
      // Inherit all lower-tier features
      "auth",
      "i18n",
      "ai-chat",
      "admin",
      "payments",
      "blog",
      "file-storage",
      "ai-workflow",
      "ai-embeddings",
      // Add enterprise features
      "ai-magic",
      "analytics",
      "emails",
      "teams",
      "feature-flags",
      "docs",
      "monitoring",
      "notifications",
      "jobs",
      "changelog",
      "roadmap",
      "waitlist",
      "seo",
      "onboarding",
      "feedback",
      "pricing",
      "search",
    ],
  },
  // Available NPM packages for each tier
  packages: {
    pronto: [
      // Core packages
      "@geenius/adapters",
      "@geenius/auth",
      "@geenius/i18n",
      "@geenius/config",
      "@geenius/errors",
      "@geenius/logger",
      "@geenius/env",
      "@geenius/convex",
      "@geenius/devtools",
      "@geenius/perf",
      "@geenius/ui",
    ],
    lancio: [
      // Inherit pronto packages
      "@geenius/adapters",
      "@geenius/auth",
      "@geenius/i18n",
      "@geenius/config",
      "@geenius/errors",
      "@geenius/logger",
      "@geenius/env",
      "@geenius/convex",
      "@geenius/devtools",
      "@geenius/perf",
      "@geenius/ui",
      // Add production packages
      "@geenius/admin",
      "@geenius/db",
      "@geenius/payments",
      "@geenius/stripe",
      "@geenius/blog",
      "@geenius/file-storage",
      "@geenius/ai",
    ],
    studio: [
      // Inherit all lower-tier packages
      "@geenius/adapters",
      "@geenius/auth",
      "@geenius/i18n",
      "@geenius/config",
      "@geenius/errors",
      "@geenius/logger",
      "@geenius/env",
      "@geenius/convex",
      "@geenius/devtools",
      "@geenius/perf",
      "@geenius/ui",
      "@geenius/admin",
      "@geenius/db",
      "@geenius/payments",
      "@geenius/stripe",
      "@geenius/blog",
      "@geenius/file-storage",
      "@geenius/ai",
      // Add enterprise packages
      "@geenius/ai-magic",
      "@geenius/ai-workflow",
      "@geenius/analytics",
      "@geenius/emails",
      "@geenius/teams",
      "@geenius/feature-flags",
      "@geenius/docs",
      "@geenius/monitoring",
      "@geenius/notifications",
      "@geenius/jobs",
      "@geenius/changelog",
      "@geenius/roadmap",
      "@geenius/waitlist",
      "@geenius/seo",
      "@geenius/onboarding",
      "@geenius/feedback",
      "@geenius/pricing",
      "@geenius/search",
      "@geenius/agent",
    ],
  },
});

/**
 * Require a feature for the current tier without throwing.
 *
 * @param tier - Current project tier
 * @param feature - Feature key to check
 * @param config - Optional tier-gate configuration, defaults to DEFAULT_TIER_GATE
 * @returns Result object describing whether the feature is available or which tier unlocks it
 */
export function requireTier(
  tier: ProjectTier,
  feature: string,
  config: TierGateConfig = DEFAULT_TIER_GATE,
): TierGateResult {
  if (config.features[tier]?.includes(feature)) {
    return { ok: true, tier, feature };
  }

  const requiredTier = TIER_ORDER.find((candidate) =>
    config.features[candidate]?.includes(feature),
  );

  if (requiredTier) {
    return {
      ok: false,
      tier,
      feature,
      requiredTier,
      reason: `Feature "${feature}" requires the ${TIER_LABELS[requiredTier]} tier.`,
    };
  }

  return {
    ok: false,
    tier,
    feature,
    reason: `Feature "${feature}" is not available in any configured tier.`,
  };
}

/**
 * Check if a specific feature is available for a given tier.
 *
 * @param feature - The feature name to check (e.g., 'payments', 'ai-chat')
 * @param tier - The tier to check against
 * @returns True if the feature is available in this tier, false otherwise
 *
 * @example
 * ```ts
 * if (isFeatureAvailable('payments', 'lancio')) {
 *   // Show payment UI
 * }
 * ```
 */
export function isFeatureAvailable(
  feature: string,
  tier: ProjectTier,
): boolean {
  return DEFAULT_TIER_GATE.features[tier]?.includes(feature) ?? false;
}

/**
 * Check if a specific package is available for a given tier.
 *
 * @param pkg - The package name to check (e.g., '@geenius/payments')
 * @param tier - The tier to check against
 * @returns True if the package is available in this tier, false otherwise
 *
 * @example
 * ```ts
 * if (isPackageAvailable('@geenius/admin', 'lancio')) {
 *   // Package can be installed
 * }
 * ```
 */
export function isPackageAvailable(pkg: string, tier: ProjectTier): boolean {
  return DEFAULT_TIER_GATE.packages[tier]?.includes(pkg) ?? false;
}

/**
 * Get all features available for a specific tier.
 * Includes features inherited from lower tiers.
 *
 * @param tier - The tier to get features for
 * @returns Array of available feature names
 *
 * @example
 * ```ts
 * const lancioFeatures = getAvailableFeatures('lancio');
 * logger.info(lancioFeatures); // ['auth', 'i18n', 'ai-chat', 'admin', ...]
 * ```
 */
export function getAvailableFeatures(tier: ProjectTier): string[] {
  return [...(DEFAULT_TIER_GATE.features[tier] || [])];
}

/**
 * Get all packages available for a specific tier.
 * Includes packages inherited from lower tiers.
 *
 * @param tier - The tier to get packages for
 * @returns Array of available package names
 *
 * @example
 * ```ts
 * const studioPackages = getAvailablePackages('studio');
 * logger.info(studioPackages.length); // 35+
 * ```
 */
export function getAvailablePackages(tier: ProjectTier): string[] {
  return [...(DEFAULT_TIER_GATE.packages[tier] || [])];
}

/**
 * Get the new features that would be unlocked by upgrading from one tier to another.
 * Useful for upgrade prompts and marketing copy.
 *
 * @param currentTier - The current tier the project is on
 * @param targetTier - The tier being upgraded to
 * @returns Array of feature names new in the target tier (not in current tier)
 *
 * @example
 * ```ts
 * const newFeatures = getUpgradeFeatures('pronto', 'lancio');
 * logger.info(newFeatures); // ['admin', 'payments', 'blog', ...]
 *
 * // Use in UI
 * showUpgradeDialog({
 *   title: 'Upgrade to Lancio',
 *   newFeatures: newFeatures,
 *   benefitsList: newFeatures.map(f => `Unlock ${f}`)
 * });
 * ```
 */
export function getUpgradeFeatures(
  currentTier: ProjectTier,
  targetTier: ProjectTier,
): string[] {
  const current = new Set(getAvailableFeatures(currentTier));
  const target = getAvailableFeatures(targetTier);
  // Return features in target that aren't in current
  return target.filter((f) => !current.has(f));
}
